import { useEffect, useRef, useState } from 'react';
import { useNavigate, Outlet } from 'react-router';
import { BrandMark } from '../../components/BrandMark.js';
import { ChangePasswordForm } from '../../components/ChangePasswordForm.js';
import { useSession } from '../../lib/session.js';

/**
 * Shell for the whole `/apply/*` tree. Deliberately not `.shell` — that's
 * the console/portal chrome (a nav bar, a badge pill, a dense header). This
 * is a linear wizard someone fills out once, not a multi-page app with
 * places to browse, so there's nowhere for a nav bar to point. See
 * styles.css's "Apply flow" section for the full visual language.
 */
export function ApplyLayout() {
  const { state, signOut } = useSession();
  const navigate = useNavigate();
  const [passwordMenuOpen, setPasswordMenuOpen] = useState(false);
  const passwordMenuRef = useRef<HTMLDivElement>(null);

  async function handleSignOut() {
    await signOut();
    void navigate('/apply', { replace: true });
  }

  useEffect(() => {
    if (!passwordMenuOpen) return;
    function onPointerDown(event: PointerEvent) {
      if (passwordMenuRef.current && !passwordMenuRef.current.contains(event.target as Node)) {
        setPasswordMenuOpen(false);
      }
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setPasswordMenuOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [passwordMenuOpen]);

  return (
    <div className="apply">
      {/* Decorative only — the slow animated blue wash behind everything.
          aria-hidden and empty, never a focus/click target (see its CSS:
          fixed, negative z-index, pointer-events: none). */}
      <div className="apply__ground" aria-hidden="true" />
      <header className="apply__header">
        {/* `<BrandMark />`, not a hand-rolled logo-or-name branch — this used
            to duplicate its logic inline. Centering (`.apply__brand`, in
            styles.css) targets any `img` nested inside it regardless of
            BrandMark's own `shell__brand-logo` class, so reusing the
            component costs nothing here. */}
        <div className="apply__brand">
          <BrandMark />
        </div>
        {state.status === 'signed-in' ? (
          <div className="apply__header-actions">
            <div className="apply-account-menu" ref={passwordMenuRef}>
              <button
                type="button"
                className="apply__signout"
                aria-expanded={passwordMenuOpen}
                onClick={() => setPasswordMenuOpen((value) => !value)}
              >
                Change password
              </button>
              {passwordMenuOpen ? (
                <div className="settings__panel card stack">
                  <ChangePasswordForm />
                </div>
              ) : null}
            </div>
            <button type="button" className="apply__signout" onClick={() => void handleSignOut()}>
              Sign out
            </button>
          </div>
        ) : null}
      </header>
      <main className="apply__main">
        <Outlet />
      </main>
    </div>
  );
}
